<template>
  <form class="admin-userform" @submit.prevent="onSubmit">
    <input-text
      v-model:value="state.email"
      :label="$t('labels.email')"
      :required="true"
      :error="fieldErrors.email"
      @update:value="$emit('clear-error', 'email')"
    />

    <input-text
      v-model:value="state.firstname"
      :label="$t('labels.firstname')"
      :required="true"
      :error="fieldErrors.firstname"
      @update:value="$emit('clear-error', 'firstname')"
    />
    <input-text
      v-model:value="state.lastname"
      :label="$t('labels.lastname')"
      :required="true"
      :error="fieldErrors.lastname"
      @update:value="$emit('clear-error', 'lastname')"
    />

    <div class="label mt-2">{{ $t("labels.language") }}</div>
    <div class="mt-1">
      <locale-chooser v-model:value="state.language" :set-current="false" />
    </div>

    <div v-if="showAdminOptions" class="mt-4">
      <div class="label">{{ $t("headings.administration") }}</div>
      <div class="column gap-2">
        <input-checkbox
          v-model:value="state.isUserAdmin"
          label="Benutzeradministrator"
        />
        <input-checkbox
          v-model:value="state.isPublisher"
          label="Kann veröffentlichen"
        />
        <input-checkbox
          v-model:value="state.isSamplemodelAdmin"
          label="Beispielmodell-Administrator"
        />
        <input-checkbox
          v-model:value="state.isBetaEnabled"
          label="Beta-Features verfügbar"
        />
        <input-checkbox
          v-model:value="state.userWasDeactivated"
          label="Nutzer deaktiviert"
        />
      </div>
    </div>

    <button type="submit" class="button mt-5" :disabled="!dataWasChanged">
      <template v-if="dataWasChanged">
        {{ submitLabel }}
      </template>
      <template v-else>
        {{ $t("labels.saved") }}
      </template>
    </button>
  </form>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { isEqual } from "lodash-es";

import InputText from "@core/input/InputText.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import LocaleChooser from "@core/input/LocaleChooser.vue";

import type { AdminUser } from "@/api/User";
import type { ValidLanguage } from "@/i18n/ValidLanguages";

const props = withDefaults(defineProps<{
  user?: AdminUser | null;
  submitLabel?: string;
  fieldErrors?: {
    email?: string;
    firstname?: string;
    lastname?: string;
  };
  showAdminOptions?: boolean;
}>(), {
  user: null,
  submitLabel: "",
  fieldErrors: () => ({}),
  showAdminOptions: true
});

const emit = defineEmits<{
  (e: "submit", userData: Partial<AdminUser>): void;
  (e: "clear-error", field: keyof typeof props.fieldErrors): void;
}>();

const lastSavedState = ref<Partial<AdminUser>>({});
const state = reactive({
  email: "",
  firstname: "",
  lastname: "",
  language: "de" as ValidLanguage,

  isUserAdmin: false,
  userWasDeactivated: false,
  isSamplemodelAdmin: false,
  isBetaEnabled: false,
  isPublisher: false
});


const dataWasChanged = computed(() => {
  return !isEqual(lastSavedState.value, state);
});

watch(() => props.user, (user) => {
  if (user) {
    state.email = user.email;
    state.firstname = user.firstname;
    state.lastname = user.lastname;
    state.language = user.language;

    state.isUserAdmin = !!user.isUserAdmin;
    state.userWasDeactivated = !!user.userWasDeactivated;
    state.isSamplemodelAdmin = user.isSamplemodelAdmin;
    state.isPublisher = user.isPublisher;
    state.isBetaEnabled = !!user.isBetaEnabled;

    updateLastSavedState();
  }
}, { immediate: true });


function onSubmit() {
  const userData = {
    ...state
  };

  emit("submit", userData);
}
function updateLastSavedState() {
  lastSavedState.value = {
    ...state
  };
}
</script>

<style lang="scss">
.admin-userform {
  max-width: 42rem;
}
</style>
